import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    CreateOperationCategoryRequest,
    UpdateOperationCategoryRequest,
} from '@/api/handlers/resources/operations/requests';
import type { GetOperationCategoriesResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

/**
 * The three writes the category management screen makes.
 *
 * All of them invalidate the **whole** operations key rather than just the
 * category list, and that is the point rather than laziness: a rename changes
 * the chip on every catalog row and every picker row, and a delete changes what
 * the editor's category control offers. A targeted invalidation would leave the
 * old name on screen everywhere except the one list that was edited.
 *
 * None of them reject: callers branch on `result.success` and read
 * `result.error.code`, which is the codebase-wide envelope contract.
 */

/**
 * Add a category. It lands last in display order.
 *
 * Returns the whole list, so the screen replaces its state from the response
 * rather than appending a row into an order it does not own.
 */
export const useCreateOperationCategoryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationCategoriesResponse>,
        Error,
        CreateOperationCategoryRequest
    >({
        mutationFn: (body) => apiClient.operations.createCategory(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};

export type UpdateOperationCategoryVariables = {
    id: string;
    body: UpdateOperationCategoryRequest;
};

/**
 * Rename a category, in both languages at once.
 *
 * Safe by construction: nothing that was filed under it moves, and no entry
 * anyone posted reads differently — a category is a label on the operation's
 * identity, not a term in its recipe.
 */
export const useUpdateOperationCategoryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<GetOperationCategoriesResponse>,
        Error,
        UpdateOperationCategoryVariables
    >({
        mutationFn: ({ id, body }) =>
            apiClient.operations.updateCategory(id, body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};

/**
 * Remove a category.
 *
 * Returns `void` on success, so the screen refetches rather than reading a list
 * back — the delete shape the whole API shares. The refusals are the interesting
 * half: `HAS_DEPENDENTS` while operations are filed under it, with the count in
 * `error.dependents.operationTemplates`, and the same code again when it is the
 * last category standing.
 */
export const useDeleteOperationCategoryMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: (id) => apiClient.operations.deleteCategory(id),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: OperationsQueryKeys.all,
                });
            }
        },
    });
};
